import React, { useEffect, useState } from "react";
import { X, Gift as GiftIcon } from "lucide-react";
import api from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

/**
 * TikTok-style gifts modal. Triggered by a floating "Send gift" button
 * on Creator profile + Watch pages. Opens Stripe checkout on selection.
 */
export default function GiftsModal({ open, onClose, creator }) {
  const [catalog, setCatalog] = useState([]);
  const [feed, setFeed] = useState([]);
  const [busy, setBusy] = useState(null);
  const [message, setMessage] = useState("");
  const { user } = useAuth();
  const nav = useNavigate();

  useEffect(() => {
    if (!open) return;
    api.get("/gifts/catalog").then((r) => setCatalog(r.data || [])).catch(() => {});
    if (creator?.user_id) {
      api.get(`/gifts/creator/${creator.user_id}/feed?limit=10`)
        .then((r) => setFeed(r.data || []))
        .catch(() => {});
    }
  }, [open, creator?.user_id]);

  if (!open) return null;

  const send = async (gift) => {
    if (!user) { toast.info("Sign in to send a gift"); return nav("/login"); }
    setBusy(gift.id);
    try {
      const { data } = await api.post("/gifts/send", {
        target_id: creator.user_id,
        gift_id: gift.id,
        message,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error("Unable to start checkout");
      setBusy(null);
    }
  };

  return (
    <div data-testid="gifts-modal" className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="tv-glass rounded-t-2xl sm:rounded-2xl w-full max-w-lg border border-white/10 overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-tv-accent font-bold">Send a gift</div>
            <div className="font-display font-bold text-white text-xl mt-0.5">to {creator?.name || "creator"}</div>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
          <input
            data-testid="gift-message-input"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            maxLength={200}
            placeholder="Add a message (optional)"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white outline-none focus:border-tv-brand text-sm"
          />

          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {catalog.map((g) => (
              <button
                key={g.id}
                data-testid={`gift-option-${g.id}`}
                disabled={busy === g.id}
                onClick={() => send(g)}
                className="tv-glass rounded-xl p-3 border border-white/5 hover:border-tv-accent/60 hover:-translate-y-0.5 transition flex flex-col items-center gap-1 disabled:opacity-50"
              >
                <div className="text-4xl leading-none">{g.emoji}</div>
                <div className="text-white text-xs font-semibold mt-1">{g.name}</div>
                <div className="text-tv-accent text-xs font-bold">${g.price_usd.toFixed(2)}</div>
              </button>
            ))}
          </div>

          {feed.length > 0 && (
            <div data-testid="gift-feed" className="mt-4">
              <div className="text-xs uppercase tracking-wider text-white/50 font-semibold mb-2 flex items-center gap-1"><GiftIcon className="w-3 h-3" /> Recent gifts</div>
              <div className="space-y-1.5 max-h-40 overflow-y-auto">
                {feed.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 text-xs text-white/70">
                    <span className="text-xl">{f.gift_emoji}</span>
                    <span className="text-white font-semibold">{f.sender_name}</span>
                    <span className="flex-1 truncate text-white/50">{f.message || "— no message —"}</span>
                    <span className="text-tv-accent font-bold">${f.amount?.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="text-[10px] text-white/40 text-center pt-2">
            Gifts are one-time purchases processed by Stripe · creators receive {85}% after platform fee
          </div>
        </div>
      </div>
    </div>
  );
}
